import OperatorPouchRepo from '@/repos/OperatorPouchRepo';
import OperatorJsonRepo from '@/repos/OperatorJsonRepo';
import { type OperatorType, type OperatorsData } from "@/types";
import { operatorsLogger } from "@/utils/loggers";
import { useOnline } from '@vueuse/core'
import HamrsApiService from '@/services/HamrsApiService';
import { OperatorNotFoundError } from '@/utils/errors/OperatorErrors';

/**
 * Safely access window.operatorsData with proper error handling
 * @throws Error if operators data is not available
 * @returns The operators data wrapper
 */
function getOperatorsDataSafely(): OperatorsData {
  if (!window.operatorsData) {
    throw new Error('Static operators data not available - app may still be initializing');
  }
  return window.operatorsData;
}

const OperatorService = {
  async initialize(): Promise<void> {
    try {
      await OperatorPouchRepo.initializeOperatorsData();
    } catch (error) {
      operatorsLogger('Error initializing operators service', error);
      throw error;
    }
  },

  // Non-blocking initialization that runs in background
  initializeInBackground(): void {
    // Fire and forget - don't await this
    this.initialize().catch(error => {
      operatorsLogger('Background initialization failed', error);
    });
  },

  /**
   * Normalize operator data to match online service format
   * Builds fullName from firstName and lastName if available
   */
  normalizeOperator(operator: OperatorType): OperatorType {
    // If we have firstName and lastName, build fullName from them
    if (operator.firstName && operator.lastName) {
      operator.fullName = `${operator.firstName} ${operator.lastName}`;
    }
    
    return operator;
  },

  /**
   * Look up an operator from local sources, optionally falling back to the
   * HAMRS API when online.
   * @param callsign the callsign to resolve
   * @param options.allowNetwork when false, never hits the API -- only the local
   *   PouchDB and bundled static data are consulted. Used by the OFFLINE lookup
   *   provider so an explicit offline selection makes no outbound requests.
   */
  async fetchByCallsign(
    callsign: string,
    options: { allowNetwork?: boolean } = {}
  ): Promise<OperatorType> {
    const { allowNetwork = true } = options;
    const normCallsign = callsign.toUpperCase();
    operatorsLogger(`Fetching operator with callsign: ${normCallsign}`);

    // Track the first genuine backend failure so a swallowed error isn't later
    // reported as a benign "not found". Each tier returns on a hit, falls
    // through on a real miss (a null result), and only records here when it
    // actually throws.
    let backendError: unknown = null;

    // First try the local database (fastest). findByCallsign returns null for a
    // genuine miss and throws only on a real backend error.
    try {
      const operator = await OperatorPouchRepo.findByCallsign(normCallsign);
      if (operator) {
        operatorsLogger('Operator found in local database');
        return this.normalizeOperator(operator);
      }
    } catch (dbError) {
      operatorsLogger('Local database lookup failed', dbError);
      backendError = dbError;
    }

    // Try static data (returns null for a miss).
    try {
      operatorsLogger('Trying static data');
      const operator = OperatorJsonRepo.findByCallsign(normCallsign);
      if (operator) {
        // Try to cache to database in background (don't await)
        OperatorPouchRepo.create(operator).catch(error => {
          operatorsLogger('Failed to cache operator to database (database may be initializing)', error);
        });
        return this.normalizeOperator(operator);
      }
      operatorsLogger(`Operator ${callsign} not found in static data`);
    } catch (staticError) {
      operatorsLogger('Error accessing static data', staticError);
      backendError = staticError;
    }

    // If now online, try API as last resort -- unless the caller opted out of
    // network access (e.g. the OFFLINE provider).
    if (allowNetwork && useOnline().value) {
      operatorsLogger('Operator not found locally, fetching from API');
      try {
        const operator = await HamrsApiService.fetchOperator(normCallsign);
        // Try to cache the operator locally for future use (don't await)
        OperatorPouchRepo.create(operator).catch(error => {
          operatorsLogger('Failed to cache API result to database', error);
        });
        return this.normalizeOperator(operator);
      } catch (apiError) {
        operatorsLogger('API fetch failed', apiError);
        backendError = apiError;
      }
    }

    // No source produced a match. Surface a real backend failure rather than
    // masquerading it as a benign miss; only a clean no-match becomes
    // OperatorNotFoundError.
    if (backendError) {
      throw backendError;
    }
    throw new OperatorNotFoundError(callsign);
  },

  async searchOperators(query: string, limit: number = 50): Promise<OperatorType[]> {
    try {
      // Try database first
      const dbResults = await OperatorPouchRepo.search(query, limit);
      if (dbResults.length > 0) {
        return dbResults;
      }
      return []; // Add explicit return to fix lint error
    } catch (error) {
      operatorsLogger('Database search failed, falling back to static data', error);

      // Fallback to static data search
      try {
        const operatorsData = getOperatorsDataSafely();
        const upperQuery = query.toUpperCase();
        const results = operatorsData.operators
          .filter(operator =>
            operator.callsign.toUpperCase().includes(upperQuery)
          )
          .slice(0, limit);

        return results;
      } catch (staticError) {
        operatorsLogger('Static data search also failed', staticError);
        throw error; // Throw original database error
      }
    }
  },

  async getOperatorsCount(): Promise<number> {
    try {
      return await OperatorPouchRepo.getCount();
    } catch (error) {
      operatorsLogger('Error getting operators count', error);
      return 0;
    }
  },

  async checkForUpdates(): Promise<void> {
    try {
      await OperatorPouchRepo.checkForUpdates();
    } catch (error) {
      operatorsLogger('Error checking for updates', error);
      throw error;
    }
  },

  async forceSync(): Promise<void> {
    try {
      await OperatorPouchRepo.forceSync();
    } catch (error) {
      operatorsLogger('Error forcing sync', error);
      throw error;
    }
  }
};

export default OperatorService;